import type { OrdersPermitGroupsCountByCategory } from '@contracts/order/OrderListResponse';
import { OrderList } from '@src/orders/domain/OrderList';
import type { OrderTab } from '@src/orders/domain/OrderTab';
import { OrdersClient } from '@src/orders/services/OrdersClient';
import { useMutation, useQuery } from '@tanstack/react-query';

export const ORDER_LISTING_FILTERED_QUERY_KEY = ['orders.listWithFilters'];
export const ORDER_LISTING_PERMITS_COUNT_BY_CATEGORY_QUERY_KEY = [
  'orders.listWithFiltersPermitsCountByCategory',
];

export type SortBy = { field: string; direction: string };

interface useOrderWithFiltersProps {
  assignedToMe: boolean;
  category: OrderTab;
  page: number;
  pageSize: number;
  searchValue?: string;
  sortBy?: SortBy;
  status?: string;
  clientId?: string;
  assigneeId?: string;
  billToClientId?: string;
  enabled?: boolean;
}

export const useOrdersWithFilters = (filters: useOrderWithFiltersProps) => {
  const { enabled = true, ...queryFilters } = filters;
  const { data: orders = new OrderList([], 0, 0), isLoading } = useQuery({
    queryKey: [...ORDER_LISTING_FILTERED_QUERY_KEY, queryFilters],
    queryFn: async () =>
      await OrdersClient.listWithFilters({
        ...queryFilters,
        limit: queryFilters.pageSize,
      }),
    enabled,
  });

  return {
    isLoading,
    orders,
  };
};

export const useOrdersPermitGroupsCountByCategory = ({
  onSuccess,
}: {
  onSuccess: (data: OrdersPermitGroupsCountByCategory[]) => void;
}) => {
  const { mutate, isPending } = useMutation({
    mutationKey: [
      ...ORDER_LISTING_PERMITS_COUNT_BY_CATEGORY_QUERY_KEY,
      onSuccess,
    ],
    mutationFn: async (data: { orderIds: string[] }) =>
      await OrdersClient.listWithFiltersPermitsCountByCategory(data),
    onSuccess,
  });

  return {
    createOrder: mutate,
    isLoading: isPending,
  };
};
